import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { post } from '../../lib/api';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';
import useTrackException from '../../lib/hooks/use-track-exception';
import useTrackEvent from '../../lib/hooks/use-track-event';
import { headerRowStyle } from '../../style';
import { BoldTextBox, StyledCaseDetailsButtons } from '../../styledComponents';
import SubmitReviewModal, { SubmitReviewModalResult } from './Modals/SubmitReviewModal';

type CosignReviewProps = {
    caseId: string;
    caseCategory: string;
    caseStage: string;
    reviewedMinutes: number;
    reviewExtendReason: string;
    handleSubmitTimeChange: (reviewedMinutes: number, reviewExtendReason: string) => void;
};

type SubmitCosignReview = {
    caseId: string;
    reviewedMinutes: number;
    reviewExtendReason: string;
    caseStage: string;
    caseCategory: string;
};

const CosignReview = ({
    caseId,
    caseStage,
    caseCategory,
    reviewedMinutes,
    reviewExtendReason,
    handleSubmitTimeChange
}: CosignReviewProps) => {
    const trackException = useTrackException();
    const trackEvent = useTrackEvent();
    const navigate = useNavigate();
    const isSmallerThanSmall = useScreenSmallerThan('sm');
    const [isReviewedValue, setIsReviewedValue] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleSubmitButton = async (data: SubmitReviewModalResult) => {
        await handleSubmit(data);
    };

    const handleSubmit = async ({
        reviewedMinutes,
        reviewExtendReason
    }: SubmitReviewModalResult) => {
        if (isReviewedValue === false) {
            toast.error('Please tick the I have reviewed button', { autoClose: 5000 });
            return false;
        }

        const cosignReviewData: SubmitCosignReview = {
            caseId: caseId,
            caseStage: caseStage,
            caseCategory: caseCategory,
            reviewedMinutes: reviewedMinutes,
            reviewExtendReason: reviewExtendReason
        };

        try {
            const result = await post('casedetails/submitcosignreview', cosignReviewData);
            if (result.data != null) {
                trackEvent('Case cosigned', {
                    caseId: caseId
                });
                toast.success('Review cosigned', { autoClose: 5000 });
                navigate('/', { replace: true });
            }
        } catch (error) {
            trackException(error as Error, {
                action: 'cosigned',
                caseId: caseId
            });
            toast.error('An error occurred while cosigning the entities', { autoClose: 5000 });
            console.log(error);
        }
    };

    return (
        <Box
            sx={{
                margin: isSmallerThanSmall ? '0' : '1.25rem'
            }}>
            <Box
                sx={{
                    background: 'white',
                    padding: '0.625rem',
                    marginBottom: '0.625rem',
                    borderRadius: '0.625rem'
                }}>
                <BoldTextBox>Cosign Review</BoldTextBox>
                <Box sx={headerRowStyle}>
                    <FormControlLabel
                        label="I have reviewed the determination above and in my oversight role agree"
                        value={isReviewedValue}
                        componentsProps={{
                            typography: { fontSize: '0.875rem', fontWeight: '700' }
                        }}
                        control={
                            <Checkbox
                                checked={isReviewedValue}
                                sx={{ padding: '0 0.75rem' }}
                                onChange={(_: unknown, isChecked: boolean) =>
                                    setIsReviewedValue(isChecked)
                                }
                            />
                        }
                    />
                </Box>
            </Box>

            <StyledCaseDetailsButtons
                variant="contained"
                onClick={() => {
                    setDialogOpen(true);
                }}>
                Submit Review
            </StyledCaseDetailsButtons>

            <SubmitReviewModal
                reviewedMinutes={reviewedMinutes}
                reviewExtendReason={reviewExtendReason}
                open={dialogOpen}
                onSubmit={handleSubmitButton}
                onClose={() => {
                    setDialogOpen(false);
                }}
                handleSubmitTimeChange={handleSubmitTimeChange}
            />
        </Box>
    );
};

export default CosignReview;
